// The date input is actually a group of inputs, a month select, a day input,
// and a year input. This helps us to avoid confusion between `dd/mm/yyyy` and
// `mm/dd/yyyy` formats.

import { type FC, useEffect, useRef, useState, useMemo } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Select, SelectItem } from "@tenant/ui";
import { InputField } from "../InputField";
import { MONTHS } from "./config";
import styles from "./styles.module.scss";
import { Logger } from "@/utils/logging";

const log = new Logger("DateInputField");

const valueAsString = (value: string | number) => {
  const number = Number(value);
  if (isNaN(number)) return "";
  if (value === 0) return "";
  return `${number}`;
};

type DateInputFieldProps = {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  errorText?: string | false;
  helperText?: string;
  onBlur?: () => void;
};

const asIntOrUndefined = (value: string) => {
  const number = parseInt(value);
  if (isNaN(number)) return undefined;
  return number;
};

const getInitialValues = (value: string) => {
  const [yyyy, mm, dd] = value.split("-");
  return [
    asIntOrUndefined(yyyy),
    asIntOrUndefined(mm),
    asIntOrUndefined(dd),
  ] as const;
};

const buildCalendarDate = (day: number, month: number, year: number) => {
  const dayString = day.toString().padStart(2, "0");
  const monthString = month.toString().padStart(2, "0");
  const yearString = year.toString().padStart(4, "0");
  return `${yearString}-${monthString}-${dayString}`;
};

export const DateInputField: FC<DateInputFieldProps> = ({
  label,
  value = "",
  errorText,
  helperText,
  onChange,
  onBlur,
}) => {
  const [block, cn] = useModuleBEM(styles, "DateInputField");
  const { t } = useI18nContext();

  const [yyyy, mm, dd] = getInitialValues(value);
  const [day, setDay] = useState<number>(dd);
  const [month, setMonth] = useState<number>(mm);
  const [year, setYear] = useState<number>(yyyy);

  const internalValueRef = useRef<string>(value);

  useEffect(() => {
    // We can only build a date if we have a day, month, and year.
    // If any are missing, we clear the value with an empty string.
    const date =
      day && month && year ? buildCalendarDate(day, month, year) : "";

    log.verbose("calendar date changed", { day, month, year, date });

    // We only want to call onChange if the value has changed.
    // This is mostly to prevent the field from showing the error text
    // when the user has not yet entered a value.
    if (date !== internalValueRef.current) {
      internalValueRef.current = date;
      onChange(date);
    }
  }, [day, month, year, onChange]);

  const monthOptions = useMemo(() => {
    return [...MONTHS].map((month, index) => ({
      value: `${index + 1}`, // 1-indexed i.e. January = 1, February = 2, etc.
      label: t(month),
    }));
  }, [t]);
  return (
    <div className={block}>
      <div className={cn("label")}>{label}</div>
      <div className={cn("container")}>
        <div className={cn("field-container")}>
          <InputField
            label={t("DateInputField.day.label")}
            placeholder={t("DateInputField.day.placeholder")}
            className={cn("day")}
            type="tel"
            value={valueAsString(day)}
            maxLength={2}
            onChangeNumber={(value) => setDay(Number(value))}
            hasInlineLabel
            onBlur={onBlur}
          />
          <Select
            label={t("DateInputField.month.label")}
            className={cn("month")}
            hasInlineLabel
            disablePortal
            value={month?.toString()}
            onValueChange={(value) => setMonth(parseInt(value))}
            onBlur={onBlur}
          >
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          <InputField
            label={t("DateInputField.year.label")}
            className={cn("year")}
            type="tel"
            hasInlineLabel
            value={valueAsString(year)}
            maxLength={4}
            onChangeNumber={(value) => setYear(Number(value))}
            onBlur={onBlur}
          />
        </div>
        {errorText && <span className={cn("error-text")}>{errorText}</span>}
        {!errorText && helperText && (
          <span className={cn("helper-text")}>{helperText}</span>
        )}
      </div>
    </div>
  );
};
